import { useEffect, useState, useCallback, useRef, useMemo, type ReactNode } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useGuidedToursPreference } from '@/features/settings/hooks/useGuidedToursPreference'
import { useTour } from '../hooks/useTour'
import { useTourProgress } from '../hooks/useTourProgress'
import { AIHintPanel } from './AIHintPanel'
import { onboardingTour } from '../tours/onboarding'
import { dashboardTour } from '../tours/dashboard'
import { clustersTour } from '../tours/clusters'
import { dagsterTour } from '../tours/dagster'
import { mcpToolsTour } from '../tours/mcp-tools'
import { aiGovernanceTour } from '../tours/ai-governance'
import { workflowsTour } from '../tours/workflows'
import { costTour } from '../tours/cost'
import { notificationsTour } from '../tours/notifications'
import { auditTour } from '../tours/audit'
import type { TourDefinition, TourUserRole } from '../types'
import { TourContext, type TourContextShape } from './TourContext'
import '../tour.css'

const ALL_TOURS: TourDefinition[] = [
  onboardingTour,
  dashboardTour,
  clustersTour,
  dagsterTour,
  mcpToolsTour,
  aiGovernanceTour,
  workflowsTour,
  costTour,
  notificationsTour,
  auditTour,
]

interface Props {
  children: ReactNode
}

export function TourProvider({ children }: Props) {
  const user = useAuthStore((s) => s.user)
  const userRole = user?.role ?? null

  // showGuidedTours is `undefined` while the preference fetch is in-flight,
  // `true`/`false` once it resolves. The auto-start effect must wait for a
  // definitive value before deciding — see the `=== undefined` guard below.
  const { showGuidedTours } = useGuidedToursPreference()

  const { progress, markCompleted, markStarted, resetProgress, completedCount } = useTourProgress()

  const [aiHintContext, setAIHintContext] = useState<string | null>(null)
  const autoStartFiredRef = useRef(false)

  const handleCompleted = useCallback(
    (tourId: string) => {
      markCompleted(tourId)
    },
    [markCompleted],
  )

  const { startTour: driveTour } = useTour(userRole, handleCompleted)

  // F3 cleanup: memoize availableTours so startTour's useCallback (which depends
  // on it) doesn't produce a new reference on every render, which would otherwise
  // cause the auto-start effect to re-run needlessly each time any state changes.
  const availableTours = useMemo(
    () =>
      ALL_TOURS.filter((tour) => {
        if (!tour.roles) return true
        return userRole ? tour.roles.includes(userRole as TourUserRole) : true
      }),
    [userRole],
  )

  const isFirstVisit = completedCount === 0

  const startTour = useCallback(
    (id: string) => {
      const tour = availableTours.find((t) => t.id === id)
      if (!tour) return
      markStarted(id)
      driveTour(tour, (ctx) => setAIHintContext(ctx))
    },
    [availableTours, driveTour, markStarted],
  )

  useEffect(() => {
    if (autoStartFiredRef.current) return
    // Temporary kill-switch (dev): VITE_DISABLE_PRODUCT_TOUR=true suppresses the
    // first-visit auto-start of the onboarding tour. Reversible — unset the env
    // var (or set it to anything other than 'true') to restore the tour.
    if (import.meta.env.VITE_DISABLE_PRODUCT_TOUR === 'true') {
      autoStartFiredRef.current = true
      return
    }
    // Defer while user is still loading.
    if (!user) return
    // B fix: preference is undefined while the server fetch is in-flight.
    // Do NOT seal the ref here — we want to re-evaluate once the value lands.
    if (showGuidedTours === undefined) return
    // Preference loaded and user has opted out — seal and exit.
    if (!showGuidedTours) {
      autoStartFiredRef.current = true
      return
    }
    if (!isFirstVisit) {
      autoStartFiredRef.current = true
      return
    }
    // W4 refire fix: if the onboarding tour was already started (even if not
    // completed), do not auto-restart it in a new tab or after a page reload.
    // The user can always restart it manually via the TourLauncher.
    if (progress['onboarding']?.startedAt) {
      autoStartFiredRef.current = true
      return
    }

    autoStartFiredRef.current = true
    const timer = setTimeout(() => {
      startTour('onboarding')
    }, 1500)
    return () => clearTimeout(timer)
  }, [user, isFirstVisit, showGuidedTours, progress, startTour])

  const value: TourContextShape = {
    availableTours,
    progress,
    startTour,
    markCompleted,
    resetProgress,
    isFirstVisit,
    userRole,
  }

  return (
    <TourContext.Provider value={value}>
      {children}
      {aiHintContext && userRole && (
        <AIHintPanel
          initialContext={aiHintContext}
          userRole={userRole}
          onClose={() => setAIHintContext(null)}
        />
      )}
    </TourContext.Provider>
  )
}
